import React, { useEffect, useMemo, useRef, useState } from 'react'
import DashboardLoader from './DashboardLoader'
import { humanizeScriptName } from '../bostonIA/lib/triage'
import { REMARK_TYPES } from '../bostonIA/lib/catalogs'

function parseJsonValue(value) {
  if (!value) return null
  if (typeof value === 'object') return value
  try {
    return JSON.parse(value)
  } catch (_) {
    return null
  }
}

function prettyJson(value) {
  if (!value) return '{}'
  if (typeof value === 'object') return JSON.stringify(value, null, 2)
  try {
    return JSON.stringify(JSON.parse(value), null, 2)
  } catch (_) {
    return String(value)
  }
}

function firstValue(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== '') || ''
}

function isBlankConfig(value) {
  if (value == null) return true
  if (typeof value === 'object') return Object.keys(value).length === 0
  const text = String(value).trim()
  return !text || text === '{}' || text === 'null' || text === 'undefined'
}

function looksLikeDataJson(value) {
  const data = parseJsonValue(value)
  if (!data || typeof data !== 'object') return false
  if (Array.isArray(data.forms)) return true
  if (Array.isArray(data.actions) && (data.title || data.messageinitial || data.postCommands)) return true
  return false
}

function isPublishMetadataOnly(value) {
  const config = parseJsonValue(value)
  if (!config || typeof config !== 'object' || Array.isArray(config)) return false
  const keys = Object.keys(config)
  return keys.length === 1 && Boolean(config.publish && typeof config.publish === 'object')
}

function normalizeOverlayConfig(value) {
  const config = parseJsonValue(value)
  if (!config || typeof config !== 'object' || Array.isArray(config)) return {}
  if (looksLikeDataJson(config) || isPublishMetadataOnly(config)) return {}
  return config
}

function emptyFieldOverride(override) {
  if (!override || typeof override !== 'object') return true
  return Object.keys(override).every((key) => override[key] === '' || override[key] == null)
}

function cleanOverlayConfig(config) {
  const next = { ...(config && typeof config === 'object' && !Array.isArray(config) ? config : {}) }
  if (next.fields && typeof next.fields === 'object' && !Array.isArray(next.fields)) {
    const fields = {}
    Object.keys(next.fields).forEach((key) => {
      if (!emptyFieldOverride(next.fields[key])) fields[key] = next.fields[key]
    })
    if (Object.keys(fields).length) next.fields = fields
    else delete next.fields
  }
  ;['hiddenFields', 'requiredFields', 'optionalFields', 'readonlyFields'].forEach((key) => {
    if (Array.isArray(next[key]) && next[key].length === 0) delete next[key]
  })
  return next
}

function fieldKey(field) {
  return String(field?.campoForm || field?.tag || field?.id || field?.name || '').trim()
}

function isRemarkConfigurableField(field) {
  if (!field || typeof field !== 'object') return false
  const mask = String(field.remark_mask || field.remark || '').trim()
  const type = String(field.typeremark || '').trim()
  const block = String(field.block ?? '').trim()
  if (type) return true
  if (['3', '5', '6', '7', '9'].includes(block)) return true
  if (/^(3|5|6|7|9|5h-|5[¥*.-]|h-|[¥*-])/i.test(mask)) return true
  return false
}

function collectConfigurableFields(dataJson) {
  const byKey = new Map()
  const forms = Array.isArray(dataJson?.forms) ? dataJson.forms : []
  forms.forEach((form) => {
    ;[...(Array.isArray(form.remarks) ? form.remarks : []), ...(Array.isArray(form.fields) ? form.fields : [])].forEach((field) => {
      const key = fieldKey(field)
      if (!key || byKey.has(key)) return
      byKey.set(key, {
        ...field,
        __formTitle: form.title || form.form || '',
      })
    })
  })
  return Array.from(byKey.values())
}

function fieldOverride(config, key) {
  return config?.fields?.[key] || {}
}

function hasListValue(config, listName, key) {
  return Array.isArray(config?.[listName]) && config[listName].includes(key)
}

function effectiveFieldValue(field, config) {
  const key = fieldKey(field)
  const override = fieldOverride(config, key)
  const hiddenByList = hasListValue(config, 'hiddenFields', key)
  const requiredByList = hasListValue(config, 'requiredFields', key)
  const readonlyByList = hasListValue(config, 'readonlyFields', key)
  const visible = override.visible !== undefined
    ? Boolean(override.visible)
    : override.hidden !== undefined
      ? !Boolean(override.hidden)
      : hiddenByList
        ? false
        : field.visible !== false
  const mandatory = override.mandatory !== undefined
    ? Boolean(override.mandatory)
    : override.required !== undefined
      ? Boolean(override.required)
      : requiredByList
        ? true
        : Boolean(field.mandatory)
  return {
    key,
    label: override.label !== undefined ? String(override.label || '') : String(field.label || key),
    visible,
    mandatory,
    readonly: override.readonly !== undefined ? Boolean(override.readonly) : readonlyByList,
    defaultValue: firstValue(override.default, override.defaultValue, override.default_value, override.value, override.dato),
    remarkConfigurable: isRemarkConfigurableField(field),
    typeremark: firstValue(override.typeremark, override.typeRemark, override.type_remark, override.remarkType, override.remark_type, override.tipoRemark, override.tipo_remark, field.typeremark),
  }
}

function publishedIndexUrl(value, script) {
  const data = parseJsonValue(value)
  return String(
    data?.publish?.url
    || data?.url
    || script?.url
    || '',
  ).trim()
}

async function dashboardFetch({ env, token, action, body, timeoutMs = 16000 }) {
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), timeoutMs)
  try {
    const response = await fetch(`${String(env.urlApi || '').replace(/\/$/, '')}/admin/ai?resource=custom-scripts&action=${encodeURIComponent(action)}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
        'x-api-key': env.key,
        token,
      },
      body: JSON.stringify(body || {}),
      signal: controller.signal,
    })
    const text = await response.text()
    let data = null
    try {
      data = text ? JSON.parse(text) : null
    } catch (_) {
      data = { message: text }
    }
    if (!response.ok || data?.ok === false) {
      throw new Error(data?.message || data?.error || `HTTP ${response.status}`)
    }
    return data
  } finally {
    window.clearTimeout(timer)
  }
}

async function fetchPublishedDataJson({ env, token, value, script }) {
  const url = publishedIndexUrl(value, script)
  if (!url) return null
  const data = await dashboardFetch({
    env,
    token,
    action: 'published-file',
    body: { source: 'dashboard_direct', url, filename: 'data.json' },
  })
  if (data?.dataJson) return data.dataJson
  return parseJsonValue(data?.content)
}

function DashboardScriptConfigModal({ open, script, session, env, token, onClose, onSaved }) {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [meta, setMeta] = useState({ name: '', description: '' })
  const [config, setConfig] = useState({})
  const [text, setText] = useState('{}')
  const [dataJson, setDataJson] = useState(null)
  const configRef = useRef({})
  const fields = useMemo(() => collectConfigurableFields(dataJson), [dataJson])

  function syncConfig(nextConfig) {
    const cleaned = cleanOverlayConfig(nextConfig)
    configRef.current = cleaned
    setConfig(cleaned)
    setText(prettyJson(cleaned))
  }

  function updateField(key, patch) {
    const currentConfig = configRef.current || {}
    syncConfig({
      ...currentConfig,
      fields: {
        ...(currentConfig.fields || {}),
        [key]: {
          ...(currentConfig.fields?.[key] || {}),
          ...patch,
        },
      },
    })
  }

  useEffect(() => {
    if (!open || !script) return
    let active = true

    async function load() {
      setLoading(true)
      setSaving(false)
      setError('')
      setMeta({ name: '', description: '' })
      syncConfig({})
      setDataJson(null)

      try {
        const loaded = await dashboardFetch({
          env,
          token,
          action: 'dashboard-config',
          body: { source: 'dashboard_config', uuid: script.uuid },
        })
        if (!active) return
        setMeta({
          name: firstValue(loaded?.name, script.name, script.namescript, script.title),
          description: firstValue(loaded?.description, script.description),
        })
        syncConfig(normalizeOverlayConfig(loaded?.config))

        const templateSource = firstValue(
          loaded?.config_template,
          loaded?.configTemplate,
          loaded?.templateConfig,
          script.config_template,
          script.configTemplate,
        )
        if (looksLikeDataJson(templateSource)) {
          setDataJson(parseJsonValue(templateSource))
          return
        }
        const published = await fetchPublishedDataJson({ env, token, value: templateSource || loaded?.url || script.url, script: { ...script, url: loaded?.url || script.url } })
        if (!active) return
        if (published && looksLikeDataJson(published)) {
          setDataJson(published)
        } else {
          setError('No se pudo leer la plantilla de campos del data.json. Puedes editar el JSON manualmente.')
        }
      } catch (loadError) {
        if (!active) return
        setError(loadError.message || 'No se pudo cargar la configuracion.')
      } finally {
        if (active) setLoading(false)
      }
    }

    load()
    return () => { active = false }
  }, [open, script, env, token])

  function formatJson() {
    try {
      const parsed = cleanOverlayConfig(JSON.parse(text))
      setError('')
      configRef.current = parsed
      setConfig(parsed)
      setText(JSON.stringify(parsed, null, 2))
    } catch (formatError) {
      setError(`JSON invalido: ${formatError.message}`)
    }
  }

  async function save() {
    let normalized = '{}'
    try {
      const parsed = cleanOverlayConfig(JSON.parse(text))
      configRef.current = parsed
      setConfig(parsed)
      setText(JSON.stringify(parsed, null, 2))
      normalized = JSON.stringify(parsed)
    } catch (parseError) {
      setError(`JSON invalido: ${parseError.message}`)
      return
    }
    setSaving(true)
    setError('')
    try {
      await dashboardFetch({
        env,
        token,
        action: 'dashboard-save-config',
        body: {
          source: 'dashboard_config',
          uuid: script.uuid,
          config: normalized,
          status: script.status ?? 1,
          name: meta.name || script.name || script.namescript || '',
        },
        timeoutMs: 20000,
      })
      if (onSaved) onSaved()
      onClose()
    } catch (saveError) {
      setError(saveError.message || 'No se pudo guardar la configuracion.')
    } finally {
      setSaving(false)
    }
  }

  if (!open || !script) return null

  const title = humanizeScriptName(String(meta.name || script.name || script.title || script.uuid || 'script'))
  const scope = `${session?.username || '-'} / ${session?.parentPcc || '-'} / ${session?.pcc || '-'}`

  return (
    <div className="dashboard-modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="dashboard-config-modal" onClick={(event) => event.stopPropagation()}>
        <div className="dashboard-config-head">
          <div>
            <span>Configuracion de usuario</span>
            <h3>{title}</h3>
          </div>
          <button type="button" className="dashboard-row-btn" onClick={onClose} disabled={saving}>Cerrar</button>
        </div>
        <div className="dashboard-config-body">
          <p className="config-scope-note">
            Estos cambios aplican solo al usuario actual: <strong>{scope}</strong>.
          </p>
          {error ? <div className="dashboard-config-alert">{error}</div> : null}
          {loading ? (
            <div className="dashboard-empty soft"><DashboardLoader text="Cargando configuracion..." /></div>
          ) : (
            <>
              {fields.length ? (
                <div className="config-field-table">
                  <div className="config-field-head">
                    <span>Campo</span>
                    <span>Visible</span>
                    <span>Obligatorio</span>
                    <span>Solo lectura</span>
                    <span>Tipo remark</span>
                    <span>Default</span>
                  </div>
                  {fields.map((field) => {
                    const view = effectiveFieldValue(field, config)
                    return (
                      <div className="config-field-row" key={view.key}>
                        <div className="config-field-main">
                          <input
                            className="config-field-label-input"
                            value={view.label}
                            onChange={(event) => updateField(view.key, { label: event.target.value })}
                            title={view.key}
                          />
                          <small>{view.key}{field.__formTitle ? ` - ${field.__formTitle}` : ''}</small>
                        </div>
                        <label className="config-toggle">
                          <input type="checkbox" checked={view.visible} onChange={(event) => updateField(view.key, { visible: event.target.checked })} />
                        </label>
                        <label className="config-toggle">
                          <input type="checkbox" checked={view.mandatory} onChange={(event) => updateField(view.key, { mandatory: event.target.checked, required: event.target.checked })} />
                        </label>
                        <label className="config-toggle">
                          <input type="checkbox" checked={view.readonly} onChange={(event) => updateField(view.key, { readonly: event.target.checked })} />
                        </label>
                        {view.remarkConfigurable ? (
                          <select
                            className="config-remark-select"
                            value={view.typeremark}
                            onChange={(event) => updateField(view.key, { typeremark: event.target.value })}
                          >
                            <option value="">Sin cambio</option>
                            {REMARK_TYPES.map((type) => (
                              <option key={type.id} value={type.id}>{type.label}</option>
                            ))}
                          </select>
                        ) : (
                          <span className="config-not-applicable">No aplica</span>
                        )}
                        <input
                          className="config-default-input"
                          value={view.defaultValue}
                          onChange={(event) => updateField(view.key, { default: event.target.value })}
                          placeholder="Sin default"
                        />
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="dashboard-config-info">No se pudo leer la plantilla de campos del data.json. Puedes editar el JSON de configuracion manualmente.</div>
              )}
              <details className="config-json-details">
                <summary>JSON de configuracion que se guardara</summary>
                <textarea
                  className="config-editor"
                  spellCheck={false}
                  value={text}
                  onChange={(event) => {
                    setText(event.target.value)
                    const parsed = parseJsonValue(event.target.value)
                    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                      const cleaned = cleanOverlayConfig(parsed)
                      configRef.current = cleaned
                      setConfig(cleaned)
                    }
                  }}
                  placeholder="{}"
                />
              </details>
            </>
          )}
        </div>
        <div className="dashboard-config-footer">
          <button type="button" className="dashboard-row-btn" onClick={formatJson} disabled={loading || saving}>Formatear</button>
          <button type="button" className="dashboard-row-btn" onClick={onClose} disabled={saving}>Cancelar</button>
          <button type="button" className="dashboard-row-btn primary" onClick={save} disabled={loading || saving}>
            {saving ? 'Guardando...' : 'Guardar configuracion'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default DashboardScriptConfigModal
